






















































































































import Vue from "vue";

import { AudioDecoderPlayer } from "@joyz/app-core/utils/audio";

import LayoutExercise from "@tt-app/layouts/LayoutExercise.vue";
import ExerciseHeaderTitle from "@tt-app/components/exercise/ExerciseHeaderTitle.vue";
import DrillBrowser from "@tt-app/components/exercise/DrillBrowser.vue";
import DrillContainer from "@tt-app/components/exercise/DrillContainer.vue";
import DrillLearnedBadgeToast from "@tt-app/components/exercise/DrillLearnedBadgeToast.vue";
import DrillProgress from "@tt-app/components/exercise/DrillProgress.vue";
import DrillQuestionListening from "@tt-app/components/exercise/DrillQuestionListening.vue";
import DrillQuestionPronunciation from "@tt-app/components/exercise/DrillQuestionPronunciation.vue";
import DrillQuestionVocabulary from "@tt-app/components/exercise/DrillQuestionVocabulary.vue";
import DrillReviewSummary from "@tt-app/components/exercise/DrillReviewSummary.vue";
import DrillReviewPronunciation from "@tt-app/components/exercise/DrillReviewPronunciation.vue";
import PracticeVocabularyDrillTrophy from "@tt-app/components/exercise/PracticeVocabularyDrillTrophy.vue";

import {
  ROUTES,
  VUEX_LEARNING_HISTORY_ACTION_NAMES,
  VUEX_LEARNING_HISTORY_NAMESPACE
} from "@tt-app/constants";
import {
  ApiExerciseBtnAction,
  DrillState,
  ExerciseViewData,
  ExerciseViewMethods,
  DrillAnswer,
  DrillQuestionAndAnswers,
  ExerciseType,
  DrillFinalScore
} from "@tt-app/models/exercise/";
import {
  drillHandler,
  exerciseHandler,
  exerciseNavHandler,
  getNewExerciseViewState
} from "@tt-app/utils/exercise";
import FeedbackPopup from "@tt-app/components/feedback/FeedbackPopup.vue";
import { audioContext } from "@tt-app/utils/audio/audioContext";

interface Data extends ExerciseViewData {
  attemptCount: number;
  audioDecoderPlayer: AudioDecoderPlayer;
  drill: DrillState;
  feedbackFormVisible: boolean;
  loading: boolean;
}

interface Methods extends ExerciseViewMethods {
  fetchLearningStats: () => void;
  goToPage: (pageIndex: number) => Promise<void>;
  handleFeedbackClose: () => void;
  initDrill: () => Promise<void>;
  onActionBtnClick: (action: ApiExerciseBtnAction) => void;
  onUserAnswer: (answer: DrillAnswer) => Promise<void>;
  onPronunciationRecordingStart: () => void;
  refreshAudioPlayer: () => void;
  updateFooter: () => void;
}

interface Computed {
  // currentQuestions: DrillAllQuestions[];
  currentDrillExercise: DrillQuestionAndAnswers | undefined;
  drillType: ExerciseType;
  feedbackRouteParams: {
    withFeedback: string;
    logId: string;
  };
  learningStats: any;
  selectedGrade: any;
  selectedGradeDetails: any;
  showFeedbackForm: boolean;
  totalWords: any;
}

/**
 * !! WARNING !!
 * - Unlike Chat based exercises, this views does not control exercise.status
 *   as this value has currently no added value
 */
export default Vue.extend<Data, Methods, Computed, {}>({
  name: "VocabularyPractice",
  components: {
    FeedbackPopup,
    DrillBrowser,
    DrillContainer,
    DrillLearnedBadgeToast,
    DrillProgress,
    DrillQuestionListening,
    DrillQuestionPronunciation,
    DrillQuestionVocabulary,
    DrillReviewSummary,
    DrillReviewPronunciation,
    PracticeVocabularyDrillTrophy,
    ExerciseHeaderTitle,
    LayoutExercise
  },

  data() {
    return {
      ...getNewExerciseViewState(4, {
        name: ROUTES.SKILLS.OVERVIEW.NAME
      }),
      attemptCount: 1,
      audioDecoderPlayer: new AudioDecoderPlayer({ audioContext }),
      drill: drillHandler.getNewState(),
      feedbackFormVisible: true,
      loading: false
    };
  },

  computed: {
    currentDrillExercise() {
      if (!this.drill.data) return undefined;
      if (this.drill.currentIndex < 0 || this.drill.currentIndex >= this.drill.exercises.length)
        return undefined;

      return this.drill.exercises[this.drill.currentIndex];
    },
    drillType() {
      return 4;
    },
    feedbackRouteParams() {
      return {
        withFeedback: Boolean(this.exercise.status !== "finished").toString(),
        logId: this.exercise.logId.toString()
      };
    },
    learningStats() {
      const learningStats = this.$store.getters["learningHistory/learningStats"];

      if (!learningStats["4"]) {
        this.fetchLearningStats();
        return undefined;
      }

      return learningStats["4"];
    },
    selectedGrade() {
      return this.selectedGradeDetails?.schoolGrade;
    },
    selectedGradeDetails() {
      const selectedSchoolGradeQueryString = this.$route.query.schoolGrade as string;

      const selectedSchoolGrade = parseInt(selectedSchoolGradeQueryString, 10);

      if (!this.learningStats) return undefined;

      return this.learningStats.learnedBadges.schoolGrades.find((grade: any) => {
        return grade.schoolGrade === selectedSchoolGrade;
      });
    },
    showFeedbackForm() {
      return this.feedbackFormVisible && this.exercise.status === "finished";
    },
    totalWords() {
      return this.selectedGradeDetails?.numWords;
    }
  },

  watch: {
    "nav.requireExitConfirmation": {
      handler(newValue) {
        window.onbeforeunload = null;

        if (newValue) {
          window.onbeforeunload = () => {
            return true;
          };
        }
      }
    },
    "drill.display": {
      async handler(newVal: string, oldVal: string) {
        if (newVal !== oldVal) {
          if (newVal === "trophy") {
            const finalScore: DrillFinalScore = {
              // first answer is considered correct
              correctCount: this.drill.exercises.filter(
                e => e.allAnswers.length > 0 && e.allAnswers[0].correction.isCorrect
              ).length,
              incorrectCount: this.drill.exercises.filter(
                e => e.allAnswers.length > 0 && !e.allAnswers[0].correction.isCorrect
              ).length,
              skippedCount: this.drill.exercises.filter(e => e.allAnswers.length === 0).length
            };

            this.exercise = await exerciseHandler.endPractice(this, this.exercise, finalScore);

            // Can exit at any time
            this.nav.requireExitConfirmation = false;
          }
        }
      }
    }
  },

  async created() {
    await this.initDrill();

    document.addEventListener("visibilitychange", this.refreshAudioPlayer);
  },

  beforeDestroy() {
    this.audioDecoderPlayer.stop();
    this.audioDecoderPlayer.destroy();

    document.removeEventListener("visibilitychange", this.refreshAudioPlayer);
  },

  beforeRouteEnter(to, from, next) {
    next((vm: Vue) => {
      vm.$data.exercise = exerciseHandler.checkAssignmentId(vm.$data.exercise, from);
    });
  },

  // https://router.vuejs.org/guide/advanced/navigation-guards.html#in-component-guards
  async beforeRouteLeave(to, from, next) {
    exerciseNavHandler.beforeRouteLeave(this.nav, state => (this.nav = state), to, from, next);
  },

  methods: {
    exit() {
      exerciseNavHandler.exit(this, this.nav, state => (this.nav = state), {
        feedbackParams: this.feedbackRouteParams
      });
    },

    onExitCancelled() {
      this.nav = exerciseNavHandler.onExitCancelled(this.nav);
    },

    onExitConfirmed() {
      exerciseNavHandler.onExitConfirm(this, this.nav, state => (this.nav = state), {
        feedbackParams: this.feedbackRouteParams
      });
    },

    fetchLearningStats() {
      this.$store.dispatch(
        `${VUEX_LEARNING_HISTORY_NAMESPACE}/${VUEX_LEARNING_HISTORY_ACTION_NAMES.LEARNING_STATS_REQUEST}`,
        { exerciseType: 4 }
      );
    },

    async goToPage(pageIndex) {
      this.drill = drillHandler.moveToPage(this.drill, pageIndex);
      await this.$nextTick();
    },

    handleFeedbackClose() {
      this.feedbackFormVisible = false;
    },

    async initDrill() {
      // (Re-)init states
      const newState = getNewExerciseViewState(this.drillType, {
        name: ROUTES.SKILLS.OVERVIEW.NAME
      });
      this.exercise = newState.exercise;
      this.nav = newState.nav;
      this.nav.requireExitConfirmation = true; // Drill requires confirmation right away

      this.drill = drillHandler.getNewState();

      // Load drills
      this.exercise.status = "loading";
      try {
        this.drill = (await drillHandler.loadPracticeData(this, this.drill, this.exercise)) as any;
      } catch (err) {
        this.loadingError = this.$__("drill.error.loading");
        this.exercise.status = "error";
        return;
      }
      this.exercise.status = "loaded";

      // Update states after data loading
      this.exercise = exerciseHandler.loadHeaderFromDrill(this.exercise, this.drill);
      this.updateFooter();
      this.exercise.footer.disabled = true;

      // Start
      this.exercise = await exerciseHandler.startPractice(this, this.exercise, this.drill);
      this.exercise.status = "in-progress";
      this.exercise.footer.disabled = false;
    },

    async onActionBtnClick(action) {
      // Start action
      if (action === "NEXT") {
        this.drill = drillHandler.moveToPage(this.drill);
        this.updateFooter();
        this.attemptCount = 1;
      } else if (action === "SKIP") {
        this.exercise.footer.disabled = true;
        this.loading = true;
        this.drill = await drillHandler.onUserSkippedPractice(this, this.exercise, this.drill);
        this.updateFooter();

        await this.$nextTick();
        this.loading = false;
        this.exercise.footer.disabled = false;
      }
      // Motivated user wants to start again
      else if (action === "RETRY") {
        await this.initDrill();
      } else if (action === "FINISH") {
        const finalScore: DrillFinalScore = {
          // first answer is considered correct
          correctCount: this.drill.exercises.filter(
            e => e.allAnswers.length > 0 && e.allAnswers[0].correction.isCorrect
          ).length,
          incorrectCount: this.drill.exercises.filter(
            e => e.allAnswers.length > 0 && !e.allAnswers[0].correction.isCorrect
          ).length,
          skippedCount: this.drill.exercises.filter(e => e.allAnswers.length === 0).length
        };

        exerciseHandler.endPractice(this, this.exercise, finalScore);

        this.nav.requireExitConfirmation = false;

        this.exit();
      }
      // Non-motivated user wants to get out here as soon as possible
      else if (action === "END") {
        this.exit();
      }
    },

    async onUserAnswer(answer) {
      this.loading = true;
      this.exercise.footer.disabled = true;

      this.drill = await drillHandler.onUserPracticeSubmit(this, this.exercise, this.drill, answer);
      this.updateFooter();

      await this.$nextTick();
      this.loading = false;
      this.exercise.footer.disabled = false;
    },

    onPronunciationRecordingStart() {
      this.exercise.footer.disabled = true;

      if (this.drill.exercises[this.drill.currentIndex].allAnswers.length > 0) {
        this.attemptCount = this.drill.exercises[this.drill.currentIndex].allAnswers.length + 1;
      }
    },

    refreshAudioPlayer() {
      if (document.visibilityState === "visible") {
        console.log("refreshing audio player");
        this.audioDecoderPlayer = new AudioDecoderPlayer({ audioContext });
      }
    },

    updateFooter() {
      this.exercise = exerciseHandler.loadFooterFromPractice(this, this.exercise, this.drill);
    }
  }
});
